import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import {DataStatus} from "@/features/application/api/applicationModels";
import metricApi from "./api/dashboardAPI";
import {
    IBusinessUnitMetricData,
    IModuleMetricDefinition,
    IMetricRequest,
    IBusinessUnitMetricDataResult
} from "./api/dashboardModels";
import {IProblemDetails} from "@/features/application/api/applicationModels";
import {HttpErrorType} from "@/util/errorUtil";

type DashboardState = {
    isGridLoading: boolean;
    fetchMetricsDataStatus: DataStatus;
    locationMetricData: IBusinessUnitMetricData[];
    metricDefinitions: IModuleMetricDefinition[];
};

// Define the initial state using that type
const initialState: DashboardState = {
    isGridLoading: false,
    fetchMetricsDataStatus: DataStatus.Undefined,
    locationMetricData: [],
    metricDefinitions: []
};

// Async thunk for fetching metrics
export const fetchMetrics = createAsyncThunk<
    IBusinessUnitMetricDataResult,
    IMetricRequest,
    {rejectValue: IProblemDetails}
>("dashboard/fetchMetrics", async (request, {rejectWithValue}) => {
    try {
        const response = await metricApi.fetchMetrics(request);
        return response;
    } catch (err) {
        return rejectWithValue(err as IProblemDetails);
    }
});

const dashboardSlice = createSlice({
    name: "dashboard",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchMetrics.pending, (state) => {
                state.isGridLoading = true;
                state.fetchMetricsDataStatus = DataStatus.Undefined;
            })
            .addCase(fetchMetrics.fulfilled, (state, action) => {
                state.isGridLoading = false;
                state.fetchMetricsDataStatus = DataStatus.Loaded;
                state.locationMetricData = action.payload.metricData || [];
                state.metricDefinitions = action.payload.metricDefinitions || [];
            })
            .addCase(fetchMetrics.rejected, (state, action) => {
                state.isGridLoading = false;
                if (action.payload?.status === 403) {
                    state.fetchMetricsDataStatus = DataStatus.Forbidden;
                } else if (action.error.name === HttpErrorType.Abort) {
                    state.fetchMetricsDataStatus = DataStatus.Undefined; // Ignore abort errors
                } else {
                    state.fetchMetricsDataStatus = DataStatus.Failed;
                }
            });
    }
});

export const actionCreators = {
    fetchMetrics
};

export default dashboardSlice.reducer;
